import { appConfig } from "./config";
import { convertCoordinateFromString, convertCoordinateToString } from "./utils";

export type HttpRequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

async function _doRequestWithApiKey(
  method: HttpRequestMethod,
  url: string,
  apiKey: string,
  data?: object,
): Promise<{
  isSuccessful: boolean;
  status?: number;
  message?: string;
  data?: any;
}> {
  try {
    const response = await fetch(
      url, {
        method: method,
        headers: {
          // Set `charset=UTF-8` to fix the error `Unsupported Media Type`
          'Content-Type': 'application/json; charset=UTF-8',
          "X-Pip-API-Key": apiKey,
        },
        body: data ? JSON.stringify(data) : null
      }
    );
    // Response with error
    if (!response.ok) {
      console.error('Request got response with error:', response);
      return {
        isSuccessful: false,
        status: response.status,
        message: response.statusText,
      }
    }
    
    // Success with no data
    if (response.status === 204) {
      return {
        isSuccessful: true,
        status: response.status,
        message: response.statusText,
      }
    }

    // Success with data
    return {
      isSuccessful: true,
      status: response.status,
      message: response.statusText,
      data: await response.json(),
    }
  } catch (error) {
    console.error('Request got exception:', error);
    return {
      isSuccessful: false,
      message: 'Network request error',
    }
  }
}

export async function _doRequest(redirectWhenUnauthorized: boolean, method: HttpRequestMethod, url: string, data?: object): Promise<any> {
  const apiKey = localStorage.getItem('apiKey');
  if (!apiKey) {
    window.location.href = '/login';
    return;
  }
  const resp = await _doRequestWithApiKey(method, url, apiKey, data);
  if (resp.isSuccessful) return resp.data;
  if (resp.status === 401 && redirectWhenUnauthorized) {
    localStorage.removeItem('apiKey');
    window.location.href = '/login';
    return;
  }
  throw new Error(resp.message);
}

export async function doGetRequest(url: string, redirectWhenUnauthorized = true): Promise<any> {
  return _doRequest(redirectWhenUnauthorized, 'GET', url);
}

export async function doPostRequest(url: string, data: object, redirectWhenUnauthorized = true): Promise<any> {
  return _doRequest(redirectWhenUnauthorized, 'POST', url, data);
}

export async function doPutRequest(url: string, data: object, redirectWhenUnauthorized = true): Promise<any> {
  return _doRequest(redirectWhenUnauthorized, 'PUT', url, data);
}

export async function doPatchRequest(url: string, data: object, redirectWhenUnauthorized = true): Promise<any> {
  return _doRequest(redirectWhenUnauthorized, 'PATCH', url, data);
}

export async function doLogin(apiKey: string): Promise<number> {
  const resp = await _doRequestWithApiKey('GET', `${appConfig.settings.pipApiBase}/customers`, apiKey);
  if (resp.status === 200) {
    localStorage.setItem('apiKey', apiKey);
    return Array.isArray(resp.data) ? resp.data.length : 0;
  } else {
    localStorage.removeItem('apiKey');
    return 0;
  }
}

export async function doLogout(): Promise<void> {
  localStorage.removeItem('apiKey');
  window.location.href = '#/login'; // Because of HashRouter, we need to use hash to redirect
}

// TODO: As the endpont can only return max 2000 events per request, we need to loop the requests to get all events.
// Once the backend supports larger data fetch, we can remove this function and use doGetRequest directly.
//
// Parameter Examples:
//  startTime: "2025-12-19T00:00:00+13:00"
//  endTime:   "2025-12-19T16:04:00+13:00"
export async function fetchEventsWithLoop(url: string, startTime?: string, endTime?: string): Promise<any[]> {
  let events: any[] = [];
  if (!startTime || !endTime) {
    return _doRequest(false, 'GET', url);
  }

  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const queryStartDate = startDate;
  let queryEndDate = endDate;
  const strStartDate = queryStartDate.toISOString();

  while (true) {
    // strStartDate:  "2025-12-18T11:00:00.000Z"
    // strEndDate:    "2025-12-19T03:04:00.000Z"
    const strEndDate = queryEndDate.toISOString();
    const fetchedEvents = await _doRequest(false, 'GET', `${url}&fromUtc=${encodeURIComponent(strStartDate)}&toUtc=${encodeURIComponent(strEndDate)}`);
    if (!fetchedEvents) break;
    events = events.concat(fetchedEvents);
    if (fetchedEvents.length < appConfig.settings.maxEventsPerRequest) {
      break;
    }
    const oldestEvent = fetchedEvents[fetchedEvents.length - 1];
    const oldestEventDate = new Date(oldestEvent.EventOccurredAt);
    if (oldestEventDate <= startDate) {
      break;
    }

    // Move forward by 1 second to avoid fetching the same event again
    queryEndDate = new Date(oldestEventDate.getTime() - 1000);
  }
  return events;
}

export async function fetchMapAddress(coordinate: string): Promise<string> {
  if (!convertCoordinateFromString(coordinate)) return '';
  try {
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordinate}&key=${appConfig.settings.googleMapApiKey}`);
    const data = await response.json();
    if (data.results.length > 0) {
      return data.results[0].formatted_address;
    }
  } catch (error) {
    console.error("Failed to find address", error);
  }
  return '';
}

export async function fetchMapCoordinate(address: string): Promise<string> {
  if (!address) return '';
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${appConfig.settings.googleMapApiKey}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
  
    if (data.status === "OK" && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      return convertCoordinateToString(lat, lng);
    } else {
      console.error("Failed to fetch position:", data.status);
    }
  } catch (error) {
    console.error("Failed to fetch position:", error);
  }
  return '';
}